/**
 * Full Inventory Management Page
 * Stock in/out, wastage tracking, reorder management
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Package, AlertTriangle, TrendingDown, ShoppingCart, Plus, Search, 
  ArrowUpCircle, ArrowDownCircle, Trash2, History, RefreshCcw, Loader2,
  Filter, Download, BarChart3, AlertCircle, CheckCircle
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import api from '@/services/api';
import { toast } from 'sonner';

// Wastage reason codes
const WASTAGE_REASONS = [
  { value: 'expired', label: 'Expired' },
  { value: 'spoiled', label: 'Spoiled/Damaged' },
  { value: 'prep_waste', label: 'Preparation Waste' },
  { value: 'customer_return', label: 'Customer Return' },
  { value: 'quality_issue', label: 'Quality Issue' },
  { value: 'spillage', label: 'Spillage/Accident' },
  { value: 'other', label: 'Other' },
];

export default function InventoryPage() {
  const { currentOutlet, outletId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [inventory, setInventory] = useState([]);
  const [movements, setMovements] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('stock');
  
  // Dialog states
  const [stockInDialogOpen, setStockInDialogOpen] = useState(false);
  const [stockOutDialogOpen, setStockOutDialogOpen] = useState(false);
  const [wastageDialogOpen, setWastageDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Form states
  const [stockForm, setStockForm] = useState({ quantity: '', notes: '', supplier: '' });
  const [wastageForm, setWastageForm] = useState({ quantity: '', reason: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  // Helper to format decimal values (API returns strings)
  const formatQty = (val, decimals = 1) => {
    const num = parseFloat(val);
    return isNaN(num) ? '-' : num.toFixed(decimals);
  };

  // Fetch inventory data
  const fetchInventory = useCallback(async () => {
    if (!outletId) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/inventory/stock?outlet_id=${outletId}`);
      setInventory(response.data || []);
    } catch (err) {
      console.error('Failed to fetch inventory:', err);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [outletId]);

  // Fetch movement history
  const fetchMovements = useCallback(async () => {
    if (!outletId) return;
    
    try {
      const response = await api.get(`/inventory/movements?outlet_id=${outletId}&limit=50`);
      setMovements(response.data || []);
    } catch (err) {
      console.error('Failed to fetch movements:', err);
    }
  }, [outletId]);

  useEffect(() => {
    fetchInventory();
    fetchMovements();
  }, [fetchInventory, fetchMovements]);

  // Stats calculations
  const stats = {
    totalItems: inventory.length,
    lowStock: inventory.filter(i => i.is_below_reorder && !i.is_critical).length,
    critical: inventory.filter(i => i.is_critical).length,
    totalValue: inventory.reduce((sum, i) => sum + (i.quantity * (i.unit_cost || 0)), 0),
  };

  // Filter inventory
  const filteredInventory = inventory.filter(item => {
    const matchesSearch = item.item_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.category?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterStatus === 'all') return matchesSearch;
    if (filterStatus === 'low') return matchesSearch && item.is_below_reorder && !item.is_critical;
    if (filterStatus === 'critical') return matchesSearch && item.is_critical;
    if (filterStatus === 'ok') return matchesSearch && !item.is_below_reorder;
    return matchesSearch;
  });

  // Handle Stock In
  const handleStockIn = async () => {
    if (!selectedItem || !stockForm.quantity) {
      toast.error('Please fill required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/inventory/movements', {
        outlet_id: outletId,
        raw_material_id: selectedItem.raw_material_id,
        movement_type: 'purchase',  // Backend expects 'purchase' for stock in
        quantity: parseFloat(stockForm.quantity),
        unit: selectedItem.unit,
        notes: stockForm.notes || `Stock received${stockForm.supplier ? ` from ${stockForm.supplier}` : ''}`,
        reference_type: 'manual_adjustment',
      });
      
      toast.success(`Added ${stockForm.quantity} ${selectedItem.unit} of ${selectedItem.item_name}`);
      setStockInDialogOpen(false);
      setStockForm({ quantity: '', notes: '', supplier: '' });
      setSelectedItem(null);
      fetchInventory();
      fetchMovements();
    } catch (err) {
      console.error('Stock in error:', err);
      toast.error(err.response?.data?.detail || 'Failed to record stock in');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Stock Out
  const handleStockOut = async () => {
    if (!selectedItem || !stockForm.quantity) {
      toast.error('Please fill required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/inventory/movements', {
        outlet_id: outletId,
        raw_material_id: selectedItem.raw_material_id,
        movement_type: 'usage',  // Backend expects 'usage' for stock out
        quantity: parseFloat(stockForm.quantity),
        unit: selectedItem.unit,
        notes: stockForm.notes || 'Stock used for operations',
        reference_type: 'manual_adjustment',
      });
      
      toast.success(`Removed ${stockForm.quantity} ${selectedItem.unit} of ${selectedItem.item_name}`);
      setStockOutDialogOpen(false);
      setStockForm({ quantity: '', notes: '', supplier: '' });
      setSelectedItem(null);
      fetchInventory();
      fetchMovements();
    } catch (err) {
      console.error('Stock out error:', err);
      toast.error(err.response?.data?.detail || 'Failed to record stock out');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Wastage
  const handleWastage = async () => {
    if (!selectedItem || !wastageForm.quantity || !wastageForm.reason) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const reasonLabel = WASTAGE_REASONS.find(r => r.value === wastageForm.reason)?.label || wastageForm.reason;
      await api.post('/inventory/movements', {
        outlet_id: outletId,
        raw_material_id: selectedItem.raw_material_id,
        movement_type: 'wastage',
        quantity: parseFloat(wastageForm.quantity),
        unit: selectedItem.unit,
        reason: wastageForm.reason,
        notes: `${reasonLabel}${wastageForm.notes ? `: ${wastageForm.notes}` : ''}`,
        reference_type: 'wastage',
      });
      
      toast.success(`Recorded wastage of ${wastageForm.quantity} ${selectedItem.unit}`);
      setWastageDialogOpen(false);
      setWastageForm({ quantity: '', reason: '', notes: '' });
      setSelectedItem(null);
      fetchInventory();
      fetchMovements();
    } catch (err) {
      console.error('Wastage error:', err);
      toast.error(err.response?.data?.detail || 'Failed to record wastage');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (item) => {
    if (item.is_critical) {
      return <Badge className="bg-red-100 text-red-700 border-red-200">Critical</Badge>;
    }
    if (item.is_below_reorder) {
      return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Low Stock</Badge>;
    }
    return <Badge className="bg-green-100 text-green-700 border-green-200">In Stock</Badge>;
  };

  const getMovementIcon = (type) => {
    switch (type) {
      case 'purchase': 
      case 'transfer_in':
      case 'return':
        return <ArrowDownCircle className="w-4 h-4 text-green-600" />;
      case 'usage': 
      case 'transfer_out':
        return <ArrowUpCircle className="w-4 h-4 text-blue-600" />;
      case 'wastage': 
        return <Trash2 className="w-4 h-4 text-red-600" />;
      case 'adjustment': 
        return <RefreshCcw className="w-4 h-4 text-purple-600" />;
      default: 
        return <History className="w-4 h-4 text-gray-600" />;
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl md:text-3xl text-gray-900">Inventory Management</h1>
          <p className="text-gray-500 text-xs sm:text-sm mt-1">
            {currentOutlet?.name || 'All Outlets'} - Stock levels, movements & wastage
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => fetchInventory()}>
            <RefreshCcw className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Export</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalItems}</p>
                <p className="text-sm text-gray-500">Total Items</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <TrendingDown className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.lowStock}</p>
                <p className="text-sm text-gray-500">Low Stock</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.critical}</p>
                <p className="text-sm text-gray-500">Critical</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <BarChart3 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalValue.toLocaleString()} NOK</p>
                <p className="text-sm text-gray-500">Stock Value</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="stock" data-testid="tab-stock">Stock Levels</TabsTrigger>
          <TabsTrigger value="movements" data-testid="tab-movements">Movements</TabsTrigger>
          <TabsTrigger value="wastage" data-testid="tab-wastage">Wastage Log</TabsTrigger>
        </TabsList>

        {/* Stock Levels Tab */}
        <TabsContent value="stock" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search inventory..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="inventory-search"
                    />
                  </div>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-40">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Items</SelectItem>
                      <SelectItem value="ok">In Stock</SelectItem>
                      <SelectItem value="low">Low Stock</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : filteredInventory.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No inventory items found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left border-b">
                        <th className="pb-3 font-medium text-gray-600">Item</th>
                        <th className="pb-3 font-medium text-gray-600">Category</th>
                        <th className="pb-3 font-medium text-gray-600 text-right">Current Stock</th>
                        <th className="pb-3 font-medium text-gray-600 text-right">Reorder Point</th>
                        <th className="pb-3 font-medium text-gray-600 text-right">Par Level</th>
                        <th className="pb-3 font-medium text-gray-600">Status</th>
                        <th className="pb-3 font-medium text-gray-600 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInventory.map((item) => (
                        <tr key={item.id} className="border-b last:border-0 hover:bg-gray-50">
                          <td className="py-3 font-medium">{item.item_name}</td>
                          <td className="py-3 text-gray-600">{item.category || '-'}</td>
                          <td className="py-3 text-right font-mono">
                            {formatQty(item.quantity)} {item.unit}
                          </td>
                          <td className="py-3 text-right text-gray-500">
                            {formatQty(item.reorder_point) || '-'}
                          </td>
                          <td className="py-3 text-right text-gray-500">
                            {formatQty(item.par_level) || '-'}
                          </td>
                          <td className="py-3">{getStatusBadge(item)}</td>
                          <td className="py-3">
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => {
                                  setSelectedItem(item);
                                  setStockInDialogOpen(true);
                                }}
                                data-testid={`stock-in-${item.id}`}
                              >
                                <ArrowDownCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => {
                                  setSelectedItem(item);
                                  setStockOutDialogOpen(true);
                                }}
                                data-testid={`stock-out-${item.id}`}
                              >
                                <ArrowUpCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => {
                                  setSelectedItem(item);
                                  setWastageDialogOpen(true);
                                }}
                                data-testid={`wastage-${item.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Movements Tab */}
        <TabsContent value="movements" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Stock Movements</CardTitle>
              <CardDescription>Recent stock in/out transactions</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                {movements.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No movement history</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {movements.map((movement) => (
                      <div
                        key={movement.id}
                        className="flex items-center gap-4 p-3 rounded-lg border hover:bg-gray-50"
                      >
                        <div className="p-2 bg-gray-100 rounded-full">
                          {getMovementIcon(movement.movement_type)}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{movement.item_name || 'Unknown Item'}</p>
                          <p className="text-sm text-gray-500">{movement.notes || '-'}</p>
                        </div>
                        <div className="text-right">
                          <p className={`font-mono font-medium ${
                            ['purchase', 'transfer_in', 'return'].includes(movement.movement_type) ? 'text-green-600' : 
                            movement.movement_type === 'wastage' ? 'text-red-600' : 'text-blue-600'
                          }`}>
                            {['purchase', 'transfer_in', 'return'].includes(movement.movement_type) ? '+' : '-'}
                            {formatQty(movement.quantity)} {movement.unit}
                          </p>
                          <p className="text-xs text-gray-400">
                            {new Date(movement.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Wastage Tab */}
        <TabsContent value="wastage" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Wastage Log</CardTitle>
              <CardDescription>Track and analyze product wastage</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                {movements.filter(m => m.movement_type === 'wastage').length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-50" />
                    <p>No wastage recorded</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {movements
                      .filter(m => m.movement_type === 'wastage')
                      .map((wastage) => (
                        <div
                          key={wastage.id}
                          className="flex items-center gap-4 p-3 rounded-lg border border-red-100 bg-red-50/30"
                        >
                          <div className="p-2 bg-red-100 rounded-full">
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{wastage.item_name || 'Unknown Item'}</p>
                            <p className="text-sm text-gray-600">{wastage.notes || '-'}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono font-medium text-red-600">
                              -{formatQty(wastage.quantity)} {wastage.unit}
                            </p>
                            <p className="text-xs text-gray-400">
                              {new Date(wastage.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Stock In Dialog */}
      <Dialog open={stockInDialogOpen} onOpenChange={setStockInDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowDownCircle className="w-5 h-5 text-green-600" />
              Stock In - {selectedItem?.item_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Quantity ({selectedItem?.unit})</Label>
              <Input
                type="number"
                step="0.1"
                placeholder="Enter quantity"
                value={stockForm.quantity}
                onChange={(e) => setStockForm({ ...stockForm, quantity: e.target.value })}
                data-testid="stock-in-quantity"
              />
              <p className="text-xs text-gray-500 mt-1">
                Current stock: {formatQty(selectedItem?.quantity)} {selectedItem?.unit}
              </p>
            </div>
            <div>
              <Label>Supplier (optional)</Label>
              <Input
                placeholder="Supplier name"
                value={stockForm.supplier}
                onChange={(e) => setStockForm({ ...stockForm, supplier: e.target.value })}
              />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Add any notes..."
                value={stockForm.notes}
                onChange={(e) => setStockForm({ ...stockForm, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStockInDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleStockIn} 
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-stock-in"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Confirm Stock In
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock Out Dialog */}
      <Dialog open={stockOutDialogOpen} onOpenChange={setStockOutDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpCircle className="w-5 h-5 text-blue-600" />
              Stock Out - {selectedItem?.item_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Quantity ({selectedItem?.unit})</Label>
              <Input
                type="number"
                step="0.1"
                max={selectedItem?.quantity}
                placeholder="Enter quantity"
                value={stockForm.quantity}
                onChange={(e) => setStockForm({ ...stockForm, quantity: e.target.value })}
                data-testid="stock-out-quantity"
              />
              <p className="text-xs text-gray-500 mt-1">
                Available: {formatQty(selectedItem?.quantity)} {selectedItem?.unit}
              </p>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Reason for stock out..."
                value={stockForm.notes}
                onChange={(e) => setStockForm({ ...stockForm, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStockOutDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleStockOut} 
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-stock-out"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Confirm Stock Out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wastage Dialog */}
      <Dialog open={wastageDialogOpen} onOpenChange={setWastageDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-600" />
              Record Wastage - {selectedItem?.item_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Quantity ({selectedItem?.unit}) *</Label>
              <Input
                type="number"
                step="0.1"
                max={selectedItem?.quantity}
                placeholder="Enter quantity"
                value={wastageForm.quantity}
                onChange={(e) => setWastageForm({ ...wastageForm, quantity: e.target.value })}
                data-testid="wastage-quantity"
              />
              <p className="text-xs text-gray-500 mt-1">
                Current stock: {formatQty(selectedItem?.quantity)} {selectedItem?.unit}
              </p>
            </div>
            <div>
              <Label>Reason *</Label>
              <Select 
                value={wastageForm.reason} 
                onValueChange={(v) => setWastageForm({ ...wastageForm, reason: v })}
              >
                <SelectTrigger data-testid="wastage-reason">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {WASTAGE_REASONS.map(reason => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Additional Notes</Label>
              <Textarea
                placeholder="Any additional details..."
                value={wastageForm.notes}
                onChange={(e) => setWastageForm({ ...wastageForm, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWastageDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleWastage} 
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-wastage"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Record Wastage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
